import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Textarea,
  Heading,
  Text,
  Stack,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  Grid,
  useToast,
  Spinner,
  Button,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import config from '../../config/config';


/**
 * NominationForm component
 * Third step of the application form for nomination questions
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Initial form data
 * @param {Function} props.onChange - Function to call when form data changes
 * @param {boolean} props.showValidation - Whether to show validation errors
 * @param {string} props.id - ID for the form element
 */
const NominationForm = ({ formData = {}, onChange, showValidation = false, id = "nomination-form" }) => {
  const toast = useToast();
  const [selectedBranch, setSelectedBranch] = useState(formData.branch || '');
  const [centersByBranch, setCentersByBranch] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState({
    branches: false,
    centers: false
  });
  const debounceTimer = useRef(null);
  
  // React Hook Form setup
  const {
    register,
    control,
    formState: { errors, isDirty },
    setValue,
    getValues,
    trigger,
    watch,
    getFieldState,
    setFocus,
  } = useForm({
    mode: 'onBlur', // Only validate when focus leaves a field
    defaultValues: {
      branch: formData.branch || '',
      center: formData.center || '',
      // Coerce potential string/number values from draft to booleans
      readRegulations: !!formData.readRegulations,
      confirmResponsibilities: !!formData.confirmResponsibilities,
      associationKnowledge: formData.associationKnowledge || '',
      socialResponsibility: formData.socialResponsibility || '',
      vision2030Programs: formData.vision2030Programs || '',
      centerIncomeIdeas: formData.centerIncomeIdeas || '',
      acceptTerms: !!formData.acceptTerms,
    },
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  // Human-friendly labels for error reporting
  const fieldLabels = {
    branch: 'الفرع',
    center: 'المركز',
    readRegulations: 'الاطلاع على اللوائح',
    confirmResponsibilities: 'تأكيد المسؤوليات',
    associationKnowledge: 'المعرفة بالجمعية',
    socialResponsibility: 'المسؤولية الاجتماعية',
    vision2030Programs: 'دورك في تحقيق رؤية 2030',
    centerIncomeIdeas: 'تنمية الموارد المالية',
    acceptTerms: 'الشروط والأحكام',
  };

  // Function to validate all fields - can be called by parent via ref
  const validateAllFields = async () => {
    // First, manually check all required fields
    const formValues = getValues();
    const requiredFields = {
      branch: formValues.branch,
      center: formValues.center,
      readRegulations: formValues.readRegulations,
      confirmResponsibilities: formValues.confirmResponsibilities,
      acceptTerms: formValues.acceptTerms,
    };
    
    // Check text fields for minimum length
    const textFields = [
      'associationKnowledge',
      'socialResponsibility',
      'vision2030Programs',
      'centerIncomeIdeas'
    ];
    
    // Force validation on all fields
    const result = await trigger();
    
    // Check if any fields are missing or invalid
    const missingFields = [];
    
    // Check required fields
    Object.entries(requiredFields).forEach(([field, value]) => {
      if (!value) {
        missingFields.push(fieldLabels[field] || field);
      }
    });
    
    // Check text fields for minimum length
    textFields.forEach(field => {
      const value = formValues[field];
      if (!value || value.trim().length < 50) {
        missingFields.push(fieldLabels[field] || field);
      }
    });
    
    if (missingFields.length > 0 || !result) {
      // Build a readable list of invalid fields using fresh field states after trigger
      const allFieldNames = Object.keys(fieldLabels);
      const invalidWithMessages = allFieldNames
        .map((name) => ({ name, state: getFieldState(name) }))
        .filter(({ state }) => state.error)
        .map(({ name, state }) => ({
          name,
          label: fieldLabels[name] || name,
          message: state.error?.message,
        }));

      const description = invalidWithMessages.length
        ? `الحقول التي تحتاج إلى تصحيح:\n- ${invalidWithMessages
            .map((f) => `${f.label}${f.message ? `: ${f.message}` : ''}`)
            .join('\n- ')}`
        : `الحقول التي تحتاج إلى تصحيح:\n- ${missingFields.join('\n- ')}`;

      // Focus first invalid field
      if (invalidWithMessages[0]?.name) {
        setFocus(invalidWithMessages[0].name, { shouldSelect: true });
      } else if (missingFields.length > 0) {
        // Try to find the first missing field and focus it
        const firstMissingFieldKey = Object.keys(fieldLabels).find(key => 
          missingFields.includes(fieldLabels[key])
        );
        if (firstMissingFieldKey) {
          setFocus(firstMissingFieldKey, { shouldSelect: true });
        }
      }

      toast({
        title: 'يرجى تصحيح الأخطاء',
        description,
        status: 'error',
        duration: 7000,
        isClosable: true,
        position: 'top',
      });
      
      return false;
    }
    
    return true;
  };
  
  // Custom register function to prevent auto-jumping but allow auto-completion
  const customRegister = (name, validation) => {
    return {
      ...register(name, validation),
      onKeyDown: (e) => {
        // Prevent form submission on Enter key
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }
    };
  };
  
  // Expose form data to parent component
  useEffect(() => {
    if (typeof onChange === 'function') {
      // Pass the current data and validation function to parent
      const validateAndUpdate = async () => {
        const isValid = await validateAllFields();
        return isValid;
      };
      
      // Send initial data and validation function to parent
      onChange(getValues(), false, validateAndUpdate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Only validate when showValidation is true (Next button clicked)
  useEffect(() => {
    if (showValidation) {
      // Manually trigger validation on all fields with detailed feedback
      validateAllFields();
    }
  }, [showValidation]);
  
  // update effect to only apply initial formData on mount, avoiding update loop
  useEffect(() => {
    // Set initial values from props only on mount
    if (Object.keys(formData).length > 0) {
      // Apply values with validation to sync error state without requiring user interaction
      const coerceBoolean = (val) => {
        // Accept true/false, 'true'/'false', 1/0, '1'/'0'
        if (typeof val === 'string') {
          const lower = val.toLowerCase();
          if (lower === 'true' || lower === '1') return true;
          if (lower === 'false' || lower === '0' || lower === '') return false;
        }
        if (typeof val === 'number') return val === 1;
        return !!val;
      };

      // Handle known fields explicitly to avoid type issues
      if ('branch' in formData && formData.branch) {
        setValue('branch', formData.branch, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
        setSelectedBranch(formData.branch); // Set selected branch state
      }
      
      if ('center' in formData && formData.center) {
        setValue('center', formData.center, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      }
      
      // Ensure boolean fields are properly coerced
      if ('readRegulations' in formData) {
        const value = coerceBoolean(formData.readRegulations);
        setValue('readRegulations', value, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      }
      
      if ('confirmResponsibilities' in formData) {
        const value = coerceBoolean(formData.confirmResponsibilities);
        setValue('confirmResponsibilities', value, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      }
      
      if ('acceptTerms' in formData) {
        const value = coerceBoolean(formData.acceptTerms);
        setValue('acceptTerms', value, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      }
      
      // Set text fields
      if ('associationKnowledge' in formData) setValue('associationKnowledge', formData.associationKnowledge || '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      if ('socialResponsibility' in formData) setValue('socialResponsibility', formData.socialResponsibility || '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      if ('vision2030Programs' in formData) setValue('vision2030Programs', formData.vision2030Programs || '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
      if ('centerIncomeIdeas' in formData) setValue('centerIncomeIdeas', formData.centerIncomeIdeas || '', { shouldDirty: false, shouldTouch: false, shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for form changes and update parent component
  useEffect(() => {
    const subscription = watch((data) => {
      if (isDirty) {
        // Use a more efficient approach to prevent UI freezing
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        
        // First update with current data immediately
        const currentErrors = Object.keys(errors).length === 0;
        onChange(data, currentErrors);
        
        // Then schedule a full validation
        debounceTimer.current = setTimeout(() => {
          // Perform a more thorough validation
          const validateForm = async () => {
            // Check all fields including special URL validation
            const allFields = getValues();
            const requiredFields = ['branch', 'center', 'readRegulations', 'confirmResponsibilities', 'acceptTerms'];
            const textFields = ['associationKnowledge', 'socialResponsibility', 'vision2030Programs', 'centerIncomeIdeas'];
            
            // Check required fields
            const missingRequired = requiredFields.some(field => !allFields[field]);
            
            // Check text fields
            const invalidTextFields = textFields.some(field => {
              const value = allFields[field];
              return !value || value.trim().length < 50;
            });
            
            const isValid = !missingRequired && !invalidTextFields && await trigger();
            onChange(data, isValid);
          };
          
          validateForm();
        }, 100);
      }
    });

    // Clean up subscription and debounce
    return () => {
      subscription.unsubscribe();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, isDirty, errors]);

  // Use hardcoded branches data from config file instead of API calls
  useEffect(() => {
    setIsLoading(prev => ({ ...prev, branches: true }));
    
    // Get branches directly from config
    if (config.organization && config.organization.branches && Array.isArray(config.organization.branches)) {
      setBranches(config.organization.branches.map(branch => ({
        id: branch.id,
        name: branch.name,
      })));
    } else {
      console.error('Config branches structure is not as expected');
      setBranches([]);
    }
    
    setIsLoading(prev => ({ ...prev, branches: false }));
  }, []);

  // Handle branch change
  const handleBranchChange = (e) => {
    const branch = e.target.value;
    setSelectedBranch(branch);
    setValue('branch', branch);
    setValue('center', ''); // Reset center when branch changes
    
    // Fetch centers for the selected branch
    fetchCentersByBranch(branch);
    
    // Trigger validation
    trigger('branch');
  };

  // Get centers by branch ID directly from config
  const fetchCentersByBranch = useCallback((branchId) => {
    if (!branchId) {
      setCentersByBranch([]);
      return;
    }
    
    setIsLoading(prev => ({ ...prev, centers: true }));
    
    // Get centers directly from config and filter by branchId
    if (config.organization && config.organization.centers && Array.isArray(config.organization.centers)) {
      // Filter centers by branchId
      const centersByBranch = config.organization.centers.filter(center => center.branchId === branchId);
      setCentersByBranch(centersByBranch);
    } else {
      console.error('Config centers structure is not as expected');
      setCentersByBranch([]);
    }
    
    setIsLoading(prev => ({ ...prev, centers: false }));
  }, []);

  // Watch branch field for changes
  const watchBranch = watch('branch');

  // Update centers when branch changes
  useEffect(() => {
    if (watchBranch) {
      setSelectedBranch(watchBranch);
      fetchCentersByBranch(watchBranch);
    }
  }, [watchBranch, fetchCentersByBranch]);
  
  // Initialize centers when component mounts with branch data
  useEffect(() => {
    // This effect runs once on mount to ensure centers are loaded for initial branch value
    if (formData.branch && !centersByBranch.length) {
      fetchCentersByBranch(formData.branch);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box id={id}>
      <Stack spacing={8}>
        <Box>
          <Heading as="h2" size="md" mb={4}>
            أسئلة الترشيح
          </Heading>
          <Text color="gray.600" mb={6}>
            يرجى الإجابة على الأسئلة التالية المتعلقة بترشيحك
          </Text>
        </Box>

        {/* Branch and Center Selection */}
        <Box mb={8}>
          <Heading as="h3" size="md" mb={4} borderBottom="1px" borderColor="gray.200" pb={2}>
            الفرع والمركز
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={6}>
            {/* Branch Selection */}
            <FormControl isInvalid={errors.branch} isRequired>
              <FormLabel htmlFor="branch">الفرع</FormLabel>
              <Select
                id="branch"
                placeholder={isLoading.branches ? 'جاري التحميل...' : 'اختر الفرع'}
                {...register('branch', {
                  required: 'يرجى اختيار الفرع',
                })}
                onChange={handleBranchChange}
                isDisabled={isLoading.branches}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
              {isLoading.branches && <Spinner size="sm" ml={2} />}
              <FormErrorMessage>
                {errors.branch && errors.branch.message}
              </FormErrorMessage>
            </FormControl>

            {/* Center Selection */}
            <FormControl isInvalid={errors.center} isRequired>
              <FormLabel htmlFor="center">المركز</FormLabel>
              <Select
                id="center"
                placeholder={
                  isLoading.centers
                    ? 'جاري تحميل المراكز...'
                    : !selectedBranch
                    ? 'اختر الفرع أولاً'
                    : centersByBranch.length === 0
                    ? 'لا توجد مراكز متاحة'
                    : 'اختر المركز'
                }
                {...register('center', {
                  required: 'يرجى اختيار المركز',
                })}
                onChange={(e) => {
                  setValue('center', e.target.value);
                  trigger('center');
                }}
                isDisabled={!selectedBranch || isLoading.centers || centersByBranch.length === 0}
              >
                {centersByBranch.map((center) => (
                  <option key={center.id} value={center.id}>
                    {center.name}
                  </option>
                ))}
              </Select>
              {isLoading.centers && <Spinner size="sm" ml={2} />}
              <FormErrorMessage>
                {errors.center && errors.center.message}
              </FormErrorMessage>
            </FormControl>
          </Grid>


        </Box>

        {/* Regulations Acknowledgment */}
        <FormControl isInvalid={errors.readRegulations} isRequired mb={6}>
          <FormLabel htmlFor="readRegulations">الاطلاع على اللوائح</FormLabel>
          <Controller
            control={control}
            name="readRegulations"
            rules={{ required: 'يجب تأكيد الاطلاع على اللوائح' }}
            render={({ field: { onChange, value, ref } }) => (
              <Checkbox
                id="readRegulations"
                isChecked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                ref={ref}
              >
                لقد قرأت واطلعت على لوائح المجلس وأتفهم المسؤوليات المترتبة على العضوية
              </Checkbox>
            )}
          />
          <FormErrorMessage>
            {errors.readRegulations && errors.readRegulations.message}
          </FormErrorMessage>
        </FormControl>

        {/* Responsibilities Confirmation */}
        <FormControl isInvalid={errors.confirmResponsibilities} isRequired mb={6}>
          <FormLabel htmlFor="confirmResponsibilities">تأكيد المسؤوليات</FormLabel>
          <Controller
            control={control}
            name="confirmResponsibilities"
            rules={{ required: 'يجب تأكيد الالتزام بالمسؤوليات' }}
            render={({ field: { onChange, value, ref } }) => (
              <Checkbox
                id="confirmResponsibilities"
                isChecked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                ref={ref}
              >
                أؤكد التزامي بحضور اجتماعات المجلس والمشاركة الفعالة في أنشطته
              </Checkbox>
            )}
          />
          <FormErrorMessage>
            {errors.confirmResponsibilities && errors.confirmResponsibilities.message}
          </FormErrorMessage>
        </FormControl>

        {/* Association Knowledge */}
        <FormControl isInvalid={errors.associationKnowledge} isRequired mb={6}>
          <FormLabel htmlFor="associationKnowledge">المعرفة بالجمعية</FormLabel>
          <Textarea
            id="associationKnowledge"
            placeholder="ماذا تعرف عن جمعية مراكز الأحياء بمنطقة مكة المكرمة"
            rows={4}
            {...customRegister('associationKnowledge', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormErrorMessage>
            {errors.associationKnowledge && errors.associationKnowledge.message}
          </FormErrorMessage>
        </FormControl>

        {/* Social Responsibility */}
        <FormControl isInvalid={errors.socialResponsibility} isRequired mb={6}>
          <FormLabel htmlFor="socialResponsibility">المسؤولية الاجتماعية</FormLabel>
          <Textarea
            id="socialResponsibility"
            placeholder="ماهي مشاركاتك في جانب المسؤولية الاجتماعية"
            rows={4}
            {...customRegister('socialResponsibility', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormErrorMessage>
            {errors.socialResponsibility && errors.socialResponsibility.message}
          </FormErrorMessage>
        </FormControl>

        {/* Vision 2030 Programs */}
        <FormControl isInvalid={errors.vision2030Programs} isRequired mb={6}>
          <FormLabel htmlFor="vision2030Programs">دورك في تحقيق رؤية 2030</FormLabel>
          <Textarea
            id="vision2030Programs"
            placeholder="ما هو الدور الذي ترى أنك ستقوم به كمرشح لتحقيق أهداف وبرامج ومبادرات الجمعية بما يتوافق مع رؤية المملكة 2030"
            rows={4}
            {...customRegister('vision2030Programs', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormHelperText>
            فضلاً اذكر مقترحات أو أفكار عملية يمكنك المساهمة بها في المجلس لخدمة الحي وتعزيز دور الجمعية
          </FormHelperText>
          <FormErrorMessage>
            {errors.vision2030Programs && errors.vision2030Programs.message}
          </FormErrorMessage>
        </FormControl>

        {/* Center Income Ideas */}
        <FormControl isInvalid={errors.centerIncomeIdeas} isRequired mb={6}>
          <FormLabel htmlFor="centerIncomeIdeas">تنمية الموارد المالية</FormLabel>
          <Textarea
            id="centerIncomeIdeas"
            placeholder="ماهي خطتك لتفعيل مجلس الحي وتنمية الموارد المالية"
            rows={4}
            {...customRegister('centerIncomeIdeas', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormErrorMessage>
            {errors.centerIncomeIdeas && errors.centerIncomeIdeas.message}
          </FormErrorMessage>
        </FormControl>

        {/* Terms and Conditions */}
        <FormControl isInvalid={errors.acceptTerms} isRequired>
          <FormLabel htmlFor="acceptTerms">الشروط والأحكام</FormLabel>
          <Controller
            control={control}
            name="acceptTerms"
            rules={{ required: 'يجب الموافقة على الشروط والأحكام' }}
            render={({ field: { onChange, value, ref } }) => (
              <RadioGroup 
                onChange={(val) => {
                  onChange(val === 'true');
                  // Force validation update immediately
                  setTimeout(() => trigger('acceptTerms'), 0);
                }} 
                value={!!value ? 'true' : 'false'}
              >
                <Stack direction="column" spacing={2} align="start">
                  <Radio value="true">
                    الإقرار و الموافقة على الشروط والأحكام وأقر بأنني:
                  </Radio>
                  <Box pr={8} pt={1}>
                    <Text mb={1}>• من ذوي السمعة الحسنة</Text>
                    <Text mb={1}>• غير محكوم علي بإدانة في جريمة مخلة بالشرف أو الأمانة مالم يكن قد رد إلي اعتباري</Text>
                    <Text>• كامل الأهلية المعتبرة شرعاً</Text>
                  </Box>
                </Stack>
              </RadioGroup>
            )}
          />
          <FormErrorMessage>
            {errors.acceptTerms && errors.acceptTerms.message}
          </FormErrorMessage>
        </FormControl>
        
      </Stack>
    </Box>
  );
};

export default NominationForm;
